import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import type { Settings, SettingsInput } from '@afiliados/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { settingsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { useCan } from '@/features/auth/store';
import { MailboxTab } from './MailboxTab';

const TIMEZONES = [
  'America/Sao_Paulo',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/Lisbon',
  'UTC',
];

export function SettingsPage() {
  const qc = useQueryClient();
  const isAdmin = useCan('settings.manage');
  const q = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get });
  const [form, setForm] = useState<SettingsInput>({});
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (q.data) {
      const { hasMailboxPassword: _h, ...rest } = q.data;
      setForm(rest);
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      settingsApi.update({ ...form, ...(password ? { mailboxPassword: password } : {}) }),
    onSuccess: () => {
      toast.success('Configurações salvas');
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });
  const clearPassword = useMutation({
    mutationFn: settingsApi.clearMailboxPassword,
    onSuccess: () => {
      toast.success('Senha removida');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  if (q.isLoading || !q.data) return <Skeleton className="h-96 w-full" />;
  const s: Settings = q.data;
  const set = <K extends keyof SettingsInput>(k: K, v: SettingsInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <PageHeader
        title="Configurações"
        description="Identidade e a caixa de e-mail que recebe os relatórios da BuyGoods"
        actions={
          isAdmin && (
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Salvar
            </Button>
          )
        }
      />

      <Tabs defaultValue="mailbox">
        <TabsList>
          <TabsTrigger value="mailbox">Caixa de e-mail</TabsTrigger>
          <TabsTrigger value="general">Geral</TabsTrigger>
        </TabsList>

        <TabsContent value="mailbox" className="grid items-start gap-4 lg:grid-cols-2">
          <MailboxTab
            form={form}
            set={set}
            settings={s}
            isAdmin={isAdmin}
            password={password}
            setPassword={setPassword}
            onClearPassword={() => clearPassword.mutate()}
          />
        </TabsContent>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Identidade</CardTitle>
              <CardDescription>
                Nome que aparece no painel e fuso usado nas datas de importação.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="companyName">Nome da empresa</Label>
                <Input
                  id="companyName"
                  value={form.companyName ?? ''}
                  onChange={(e) => set('companyName', e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Fuso horário</Label>
                <Select
                  value={form.timezone ?? s.timezone}
                  onValueChange={(v) => set('timezone', v)}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
